import { useCallback, useState } from 'react';
import {
  TTimelineItemLegacyType,
  TTimelineItemStatusLegacy,
} from '../components/TimelineLegacy/TimelineLegacy';

const useTimelineLegacy = ({ items }: { items: TTimelineItemLegacyType[] }) => {
  const [steps, setSteps] = useState<TTimelineItemLegacyType[]>(items);

  const setActiveStep = (itemIndex: number) => {
    setSteps((prev) =>
      prev.map((step, index) => ({
        ...step,
        ...(index === itemIndex
          ? { status: TTimelineItemStatusLegacy.ACTIVE }
          : step.isCompleted
            ? { status: TTimelineItemStatusLegacy.COMPLETED }
            : {}),
      }))
    );
  };

  const markStepAsCompleted = (itemIndex: number, disable = false) => {
    setSteps((prev) => {
      // First, mark the current step and all previous steps as completed
      const updatedSteps = prev.map((step, index) => {
        if (index <= itemIndex) {
          return {
            ...step,
            isCompleted: true,
            status: TTimelineItemStatusLegacy.COMPLETED,
            ...(disable ? { disabled: true } : {}),
          };
        }
        return step;
      });

      // Find the next visible step after the completed step
      let nextVisibleStepIndex = -1;
      for (let i = itemIndex + 1; i < updatedSteps.length; i += 1) {
        if (!updatedSteps[i].isHidden) {
          nextVisibleStepIndex = i;
          break;
        }
      }

      // If we found a next visible step, mark it as active
      if (nextVisibleStepIndex !== -1) {
        updatedSteps[nextVisibleStepIndex] = {
          ...updatedSteps[nextVisibleStepIndex],
          status: TTimelineItemStatusLegacy.ACTIVE,
        };
      }

      return updatedSteps;
    });
  };

  const resetFurtherSteps = (itemIndex: number, disable = false) => {
    setSteps((prev) =>
      prev.map((step, index) => {
        if (index === itemIndex) {
          return {
            ...step,
            isCompleted: false,
            status: TTimelineItemStatusLegacy.ACTIVE,
            ...(disable ? { disabled: true } : {}),
          };
        }
        if (index >= itemIndex + 1) {
          return {
            ...step,
            isCompleted: false,
            status: TTimelineItemStatusLegacy.NOT_STARTED,
            ...(disable ? { disabled: true } : {}),
          };
        }

        return step;
      })
    );
  };

  const hideStep = useCallback((itemIndex: number, disable = false) => {
    setSteps((prevSteps) => {
      // Only update if the step isn't already hidden
      if (prevSteps[itemIndex]?.isHidden) return prevSteps;

      return prevSteps.map((step, index) => {
        if (index === itemIndex) {
          return {
            ...step,
            ...(disable ? { disabled: true } : {}),
            isHidden: true,
          };
        }
        return step;
      });
    });
  }, []);

  const unhideStep = useCallback((itemIndex: number, disable = false) => {
    setSteps((prevSteps) => {
      // Only update if the step is currently hidden
      if (!prevSteps[itemIndex]?.isHidden) return prevSteps;

      return prevSteps.map((step, index) => {
        if (index === itemIndex) {
          return {
            ...step,
            ...(disable ? { disabled: true } : {}),
            isHidden: false,
          };
        }
        return step;
      });
    });
  }, []);

  const activeStep =
    steps.find((step) => step.status === TTimelineItemStatusLegacy.ACTIVE) ||
    (steps[steps.length - 1].status === TTimelineItemStatusLegacy.COMPLETED
      ? steps[steps.length - 1]
      : steps[0]);

  return {
    steps,
    markStepAsCompleted,
    resetFurtherSteps,
    setActiveStep,
    activeStep,
    hideStep,
    unhideStep,
  };
};

export { useTimelineLegacy };
